import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import logo from '../assets/image/logo_web.png';
import LanguageSwitcher from './LanguageSwitcher';

export default function Navbar({ user, userRole }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
      setMobileMenuOpen(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const isActive = (path) => location.pathname === path;

  const handleNavigate = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => handleNavigate('/')}>
            <img src={logo} alt="Build My Checks Logo" className="h-12 w-auto" />
            <h1 className="text-2xl font-bold text-primary">Build My Checks</h1>
          </div>

          {/* Mobile: Language + Hamburger */}
          <div className="flex items-center gap-2 md:hidden">
            <LanguageSwitcher />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-md text-gray-700 hover:bg-gray-100"
              aria-label="Toggle menu"
            >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {user ? (
              // Logged in navigation
              <>
                <button
                  onClick={() => navigate('/dashboard')}
                  className={`font-medium transition-colors ${
                    isActive('/dashboard')
                      ? 'text-primary'
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {t('nav.dashboard')}
                </button>
                {userRole === 'admin' && (
                  <>
                    <button
                      onClick={() => navigate('/users')}
                      className={`font-medium transition-colors ${
                        isActive('/users')
                          ? 'text-primary'
                          : 'text-gray-700 hover:text-primary'
                      }`}
                    >
                      {t('nav.users')}
                    </button>
                    <button
                      onClick={() => navigate('/subscription')}
                      className={`font-medium transition-colors ${
                        isActive('/subscription')
                          ? 'text-primary'
                          : 'text-gray-700 hover:text-primary'
                      }`}
                    >
                      {t('nav.subscription')}
                    </button>
                  </>
                )}
                <LanguageSwitcher />
                <button
                  onClick={handleLogout}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  {t('nav.logOut')}
                </button>
              </>
            ) : (
              // Public navigation
              <>
                <button
                  onClick={() => handleNavigate('/features')}
                  className={`font-medium transition-colors ${
                    isActive('/features')
                      ? 'text-primary'
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {t('nav.features')}
                </button>
                <button
                  onClick={() => handleNavigate('/pricing')}
                  className={`font-medium transition-colors ${
                    isActive('/pricing')
                      ? 'text-primary'
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {t('nav.pricing')}
                </button>
                <button
                  onClick={() => handleNavigate('/contact')}
                  className={`font-medium transition-colors ${
                    isActive('/contact')
                      ? 'text-primary'
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {t('nav.contact')}
                </button>
                <button
                  onClick={() => handleNavigate('/delete-account')}
                  className={`font-medium transition-colors ${
                    isActive('/delete-account')
                      ? 'text-primary'
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {t('nav.deleteAccount')}
                </button>
                <button
                  onClick={() => handleNavigate('/guide')}
                  className={`font-medium transition-colors ${
                    isActive('/guide')
                      ? 'text-primary'
                      : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {t('nav.howToGuide')}
                </button>
                <a
                  href="https://buildmyshifts.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-1"
                >
                  BuildMyShifts
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                </a>
                <LanguageSwitcher />
                <button
                  onClick={() => handleNavigate('/login')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg font-medium transition-colors shadow-md"
                >
                  {t('nav.logIn')}
                </button>
                <button
                  onClick={() => handleNavigate('/register')}
                  className="bg-primary hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  {t('nav.getStarted')}
                </button>
              </>
            )}
          </nav>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t pt-4">
            {user ? (
              <div className="flex flex-col space-y-3">
                <button
                  onClick={() => handleNavigate('/dashboard')}
                  className={`text-left px-4 py-2 rounded-lg font-medium transition-colors ${
                    isActive('/dashboard')
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('nav.dashboard')}
                </button>
                {userRole === 'admin' && (
                  <>
                    <button
                      onClick={() => handleNavigate('/users')}
                      className={`text-left px-4 py-2 rounded-lg font-medium transition-colors ${
                        isActive('/users')
                          ? 'bg-primary text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {t('nav.users')}
                    </button>
                    <button
                      onClick={() => handleNavigate('/subscription')}
                      className={`text-left px-4 py-2 rounded-lg font-medium transition-colors ${
                        isActive('/subscription')
                          ? 'bg-primary text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {t('nav.subscription')}
                    </button>
                  </>
                )}
                <button
                  onClick={handleLogout}
                  className="text-left px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  {t('nav.logOut')}
                </button>
              </div>
            ) : (
              <div className="flex flex-col space-y-3">
                <button
                  onClick={() => handleNavigate('/features')}
                  className={`text-left px-4 py-2 rounded-lg font-medium transition-colors ${
                    isActive('/features')
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('nav.features')}
                </button>
                <button
                  onClick={() => handleNavigate('/pricing')}
                  className={`text-left px-4 py-2 rounded-lg font-medium transition-colors ${
                    isActive('/pricing')
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('nav.pricing')}
                </button>
                <button
                  onClick={() => handleNavigate('/contact')}
                  className={`text-left px-4 py-2 rounded-lg font-medium transition-colors ${
                    isActive('/contact')
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t('nav.contact')}
                </button>
                  <button
                    onClick={() => handleNavigate('/delete-account')}
                    className={`text-left px-4 py-2 rounded-lg font-medium transition-colors ${
                      isActive('/delete-account')
                        ? 'bg-primary text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {t('nav.deleteAccount')}
                  </button>
                <button                    onClick={() => handleNavigate('/guide')}
                    className={`text-left px-4 py-2 rounded-lg font-medium transition-colors ${
                      isActive('/guide')
                        ? 'bg-gray-100 text-primary'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {t('nav.howToGuide')}
                  </button>                  <a
                    href="https://buildmyshifts.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-left px-4 py-2 rounded-lg font-medium text-blue-600 hover:bg-blue-50 transition-colors flex items-center gap-1"
                  >
                    BuildMyShifts
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  </a>                  <button                  onClick={() => handleNavigate('/login')}
                  className="text-left px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors shadow-md"
                >
                  {t('nav.logIn')}
                </button>
                <button
                  onClick={() => handleNavigate('/register')}
                  className="text-left px-4 py-2 bg-primary hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  {t('nav.getStarted')}
                </button>

              </div>
            )}
          </nav>
        )}
      </div>
    </header>
  );
}

